// eslint-disable-next-line no-restricted-imports
import { OpenPosiontionNoteResponse } from '@eih-netbroker/api-position';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { IConfig } from '../../config';
import { PositionResource } from '../../resources';
import { OpenPositionGridItem, OpenPositions, openPositionsAtom } from '../../state';
import {
  createInstrumentorListedInstrumentFromInventory,
  CurrencyCross,
  CurrencyCrossObj,
  InstrumentCategoryEnum,
  InstrumentStore,
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  Inventory,
  ListedInstrument,
  ListedInstrumentLike,
  SelectedAccountOrClient,
} from '../../types';
import {
  groupBy,
  groupByAndMergeNumericValues,
  isDefined,
  Loadable,
  LoadableState,
  mergeNumericValues,
  MergeNumericValuesSettings,
  NbLogger,
  ObjectSet,
} from '../../utils';
import { BaseCurrency } from '../../utils/instrumentUtils';
import { useCurrencyExchangeRateRT } from '../core';
import { GetListedInstrumentList } from '../instrument';
import { getInitialPNl, htmlNanoId, useInventoryCommon } from '../portfolio';
import { GetResourceHeaderForMultiAccount, GetResourceHeaderWithToken } from '../useResource';
import { useOpenPositionsNotesCommon } from './useOpenPositionsNotesCommon';

const subElementGroupByAdditionalProps: MergeNumericValuesSettings<Inventory> = {
  onlyFirstValueFields: ['exchangeRate', 'decimalPlace', 'marketPriceSingle'],
  avgFields: ['initValue'],
  weightedAvgField: 'position',
};

export type UseOpenPositionsCommonParams = {
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  getResourceHeaderForMultiAccount: GetResourceHeaderForMultiAccount;
  positionResource: PositionResource;
  logger: NbLogger;
  config: IConfig;
  loadInstrument: (
    instruments: Array<ListedInstrument | ListedInstrumentLike>,
    dataScope?: Array<InstrumentSubscriptionDataScope>,
  ) => Promise<ObjectSet<InstrumentStoreItem>>;
  getExchangeRate: (currencyCrosses: Array<CurrencyCrossObj>) => Promise<Map<CurrencyCross, number>>;
  getListedInstrumentList: GetListedInstrumentList;
};

export const useOpenPositionsCommon = ({
  getResourceHeaderWithToken,
  getResourceHeaderForMultiAccount,
  positionResource,
  logger,
  config,
  loadInstrument,
  getListedInstrumentList,
  getExchangeRate,
}: UseOpenPositionsCommonParams) => {
  const { inventoryDetails } = useInventoryCommon({
    positionResource,
    getResourceHeaderWithToken,
    logger,
    config,
    loadInstrument,
    getListedInstrumentList,
    getExchangeRate,
  });
  const [openPositions, setOpenPositions] = useAtom(openPositionsAtom);
  const { getNotes } = useOpenPositionsNotesCommon({
    getResourceHeaderWithToken,
    getResourceHeaderForMultiAccount,
    positionResource,
  });
  const { getCurrencyExchangeRateRealTime } = useCurrencyExchangeRateRT();

  /**
   * Creates a grid item from an inventory
   * @private
   */
  const createGridItem = useCallback(
    (
      inventory: Inventory,
      listedInstrument: ListedInstrument | null,
      accumulatedValues:
        | undefined
        | {
            position: number;
            initValue: number;
            initPriceValue: number;
            marketPriceSingle: number;
            grossProfitLossHuf: number;
            grossProfitLoss: number;
          },
      notes: Array<OpenPosiontionNoteResponse>,
      isMergedPositions: boolean,
      isMultipleAccountSelected: boolean,
      closePrice?: number,
    ): OpenPositionGridItem => {
      const position = accumulatedValues?.position || inventory.position;
      const note = notes.find(note => note.ric === listedInstrument?.ric && note.accountId === inventory.accountNumber);

      const grossProfitLossHuf = accumulatedValues?.grossProfitLossHuf || inventory.grossProfitLossHuf;
      const grossProfitLoss = accumulatedValues?.grossProfitLoss || inventory.grossProfitLoss;
      const marketPriceSingle = accumulatedValues?.marketPriceSingle || inventory.marketPriceSingle;
      const initPriceValue = accumulatedValues?.initPriceValue || inventory.initPriceValue;

      const rate = getCurrencyExchangeRateRealTime(inventory.initCcy, inventory.marketPriceCcy);
      const { pNL, pNLPercentage } = getInitialPNl({
        instrumentCategory: inventory.instrumentCategory as InstrumentCategoryEnum, // no need to depend on accumulatedValues, because this field is not accumulated
        position,
        marketPriceSingle,
        initPriceValue,
        rate,
        grossProfitLoss: inventory.initCcy === BaseCurrency ? grossProfitLossHuf : grossProfitLoss,
        ric: inventory.ric,
        closePrice,
      });

      return {
        id: isDefined(accumulatedValues) ? inventory.id : htmlNanoId(),
        accountId: isMergedPositions ? undefined : inventory.accountNumber,
        ...createInstrumentorListedInstrumentFromInventory(inventory, listedInstrument),
        position,
        initValue: accumulatedValues?.initValue || inventory.initValue,
        initPriceValue: accumulatedValues?.initPriceValue || inventory.initPriceValue,
        value: position * (accumulatedValues?.marketPriceSingle || inventory.marketPriceSingle) * rate,
        pNLDaily: undefined, // we need real time data for this
        pNL: pNL,
        mic: inventory.marketMICCode,
        currency: inventory.initCcy,
        instrumentName: inventory.instrumentName + (inventory.namePostfix ?? ''),
        pNLPercentage: pNLPercentage,
        $subElements: undefined,
        note: note?.content,
        noteModifiedAt: note?.modifiedAt,
        inventoryDate: inventory.inventoryDate || undefined,
        isMultipleAccountSelected: isMultipleAccountSelected,
        initCcy: inventory.initCcy,
      };
    },
    [getCurrencyExchangeRateRealTime],
  );

  /**
   * Loads the open positions from the inventory list
   */
  const refreshOpenPositions = useCallback(
    async (
      accountOrClient: SelectedAccountOrClient,
      instrumentStore: InstrumentStore,
      withNotes = true,
    ): Promise<LoadableState<OpenPositions>> => {
      if (!inventoryDetails.isLoaded) {
        if (openPositions.isLoading) {
          return openPositions;
        }
        const res = Loadable.loading;
        setOpenPositions(res);
        return res;
      }

      let notes = [] as Array<OpenPosiontionNoteResponse>;

      if (withNotes) {
        setOpenPositions(Loadable.loading);
        notes = await getNotes();
      }

      // filter open positions based on the selected account number
      const filteredInventories = isDefined(accountOrClient.account)
        ? (inventoryDetails.tradableInventories || []).filter(
            _ => _.accountNumber === accountOrClient.account!.accountNumber,
          )
        : (inventoryDetails.tradableInventories || []).filter(_ => _.clientId === accountOrClient.client!);

      // load instruments
      const inventoryItemsGroupedByInstrument: Record<string, Array<Inventory>> = groupBy(filteredInventories, 'id');
      const inventoryInstrumentIds = Object.keys(inventoryItemsGroupedByInstrument).filter(_ => _ !== 'undefined');

      const gridItems = Object.values(inventoryInstrumentIds).reduce((res, id) => {
        // not tradeable instrument should not be displayed

        // find all the inventories of the instrument and merge them
        const inventoriesOfInstrument = isDefined(accountOrClient.client)
          ? groupByAndMergeNumericValues(
              inventoryItemsGroupedByInstrument[id],
              'accountNumber',
              subElementGroupByAdditionalProps,
            )
          : inventoryItemsGroupedByInstrument[id];

        // add to the open positions list the accumulated values as an OpenPositionGridItem
        if (inventoriesOfInstrument.length === 1) {
          res.push(
            createGridItem(
              inventoriesOfInstrument[0],
              inventoriesOfInstrument[0].instrument as ListedInstrument,
              {
                position: inventoriesOfInstrument[0].position,
                marketPriceSingle: inventoriesOfInstrument[0].marketPriceSingle,
                initValue: inventoriesOfInstrument[0].initValue,
                initPriceValue: inventoriesOfInstrument[0].initPriceValue,
                grossProfitLossHuf: inventoriesOfInstrument[0].grossProfitLossHuf,
                grossProfitLoss: inventoriesOfInstrument[0].grossProfitLoss,
              },
              notes,
              false,
              isDefined(accountOrClient.client),
              instrumentStore.get(inventoriesOfInstrument[0].ric)?.closePrice,
            ),
          );
        } else if (inventoriesOfInstrument.length > 1) {
          const accumulatedValues = mergeNumericValues(inventoriesOfInstrument, subElementGroupByAdditionalProps);
          const createdItem = createGridItem(
            inventoriesOfInstrument[0],
            inventoriesOfInstrument[0].instrument as ListedInstrument,
            accumulatedValues,
            // when account is selected we want to display the notes
            isDefined(accountOrClient.account) ? notes : [],
            !isDefined(accountOrClient.account),
            isDefined(accountOrClient.client),
            instrumentStore.get(inventoriesOfInstrument[0].ric)?.closePrice,
          );
          createdItem.$subElements = inventoriesOfInstrument.map(_ =>
            createGridItem(
              _,
              inventoriesOfInstrument[0].instrument as ListedInstrument,
              undefined,
              notes,
              false,
              isDefined(accountOrClient.client),
              instrumentStore.get(inventoriesOfInstrument[0].ric)?.closePrice,
            ),
          );
          res.push(createdItem);
        }
        return res;
      }, [] as Array<OpenPositionGridItem>);

      const res = Loadable.loaded({
        positions: gridItems,
      });
      setOpenPositions(res);
      return res;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no need for createGridItem
    [inventoryDetails, setOpenPositions, getListedInstrumentList, getNotes],
  );

  return useMemo(
    () => ({
      openPositions,
      refreshOpenPositions,
    }),
    [openPositions, refreshOpenPositions],
  );
};
